import SegmentIO from 'reporting/SegmentIO';
import HttpClient from 'server/helpers/HttpClient';
const httpClient = HttpClient.getInstance();
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
const splunkReporter = SplunkReporter.getInstance();
import { formatPaymentMethod } from 'shared/clientUtils';
import { ViewType } from 'shared/types';
import {
  isWalletTypeOfBank,
  isWalletTypeOfCreditCard,
  isValidPaymentMethod,
} from 'businessLogic/Wallet/helpers';

/**
 * Check if the payment method is a card-based payment (credit/debit/Apple Pay)
 * This aligns with server-side PaymentRetryExperiment targeting which excludes only ACH/bank payments
 * @see server/routes/rest/pay/helpers.js - isUSACHPayment
 * @param {string} paymentMethodType - The payment method type
 * @returns {boolean} True if payment is card-based
 */
const isCardPayment = (paymentMethodType) => {
  return ['cc', 'dc', 'dc,cc', 'amex', 'ap'].includes(paymentMethodType);
};

/**
 * Get stored payment method information from user wallets
 * @param {Array} userWallets - Array of user wallet objects
 * @param {Object} featureFlags - Feature flags object
 * @returns {Object} Object containing payment method availability info
 */
export const getStoredPaymentMethodInfo = (userWallets, featureFlags = {}) => {
  if (!userWallets || !Array.isArray(userWallets) || userWallets.length === 0) {
    return {
      has_stored_payment_methods: false,
      stored_payment_methods_count: 0,
      stored_payment_method_types: null,
      has_valid_payment_method: false,
    };
  }

  const hasCards = userWallets.some((w) => isWalletTypeOfCreditCard(w.walletType));
  const hasBanks = userWallets.some((w) => isWalletTypeOfBank(w.walletType));

  // Only perform detailed validation when feature flag is enabled, otherwise assume valid (legacy behavior)
  const hasValidPaymentMethod = featureFlags?.['SBSEG-CP-cp-fetch-wallets-before-page-view']
    ? userWallets.some(isValidPaymentMethod)
    : true;

  // Determine payment method types
  let paymentMethodTypes = null;
  if (hasCards && hasBanks) {
    paymentMethodTypes = 'cc,bank';
  } else if (hasCards) {
    paymentMethodTypes = 'cc';
  } else if (hasBanks) {
    paymentMethodTypes = 'bank';
  }

  return {
    has_stored_payment_methods: true,
    stored_payment_methods_count: userWallets.length,
    stored_payment_method_types: paymentMethodTypes,
    has_valid_payment_method: hasValidPaymentMethod,
  };
};

export async function reportPageView({
  pageName,
  sale,
  insight,
  wallet,
  payment,
  auth,
  config,
  ixp,
  featureFlags,
  viewType = ViewType.WEB,
}) {
  if (
    [
      'invoice',
      'estimate',
      'paymentReq',
      'estimatePending',
      'depositPending',
      'subscription',
    ].indexOf(pageName) >= 0
  ) {
    await reportViewd({
      sale,
      wallet,
      payment,
      ixp,
      insight,
      auth,
      config,
      viewType,
      featureFlags,
    });
  } else if (pageName === 'PaymentSuccess') {
    reportPaymentSuccess({ payment, insight, sale, wallet, ixp });
  }
}

async function reportViewd({ insight, auth, config, viewType, wallet, featureFlags }) {
  const { entityId, ticket, realmId, authToken, isUserSignedIn } = auth;

  const { portal } = config;

  // Add stored payment method tracking for signed-in users
  const trackingProps = {};
  if (isUserSignedIn && wallet?.userWallets) {
    const paymentMethodInfo = getStoredPaymentMethodInfo(wallet.userWallets, featureFlags);
    Object.assign(trackingProps, paymentMethodInfo);
  }

  SegmentIO.transactionViewed(trackingProps);

  const { growthRightHandSide } = insight;
  if (growthRightHandSide) {
    const { experimentId, treatmentKey } = growthRightHandSide;
    SegmentIO.experimentMapped(experimentId, treatmentKey);
  }

  const { appSourceOffering, domainId, token } = insight;
  try {
    const data = { status: 'VIEWED', viewType, appSourceOffering };
    const url = `/${portal}/rest/invoice/${token}/view`;
    const endpoint = '/rest/invoice/:token';
    const requestHeaders = {
      Accept: 'application/json, text/javascript, */*; q=0.01',
      'Intuit-DomainId': domainId,
      'Intuit-IntuitId': entityId,
      'Intuit-RealmId': realmId,
      'Intuit-ACSToken': token,
      sessionTicket: ticket,
      Authorization: `Bearer ${authToken}`,
    };

    await httpClient({
      url,
      method: 'POST',
      data,
      headers: requestHeaders,
      endpoint,
    });
  } catch (error) {
    return;
  }
}

function reportPaymentSuccess({ payment, sale, ixp = {} }) {
  const {
    lastSuccessfulPaymentResponse,
    savePaymentMethodSuccessful,
    paymentMethodSubType,
    inputAmount,
  } = payment;

  let amountPaid, trackingId;
  if (lastSuccessfulPaymentResponse) {
    amountPaid = lastSuccessfulPaymentResponse.amountPaid || inputAmount;
    trackingId = lastSuccessfulPaymentResponse.trackingId;
  }

  const {
    currencyInfo: { currency },
  } = sale;

  const { payPalProcessor = 'appconnect', isPayPalCommerceInvoice = false } = payment;

  // Build payment retry info object for card payments only
  let paymentRetryInfo;
  if (payment.isPaymentRetry && isCardPayment(payment.paymentMethodType)) {
    const PaymentRetryExperiment = ixp?.PaymentRetryExperiment;
    const testName =
      PaymentRetryExperiment?.experimentKey === 'IXP2_541549'
        ? `${PaymentRetryExperiment.experimentId}:${PaymentRetryExperiment.treatmentKey}`
        : '';

    paymentRetryInfo = {
      isPaymentRetry: true,
      originalTotalAmount: payment.originalPaymentAmount,
      originalPaymentMethod: payment.originalPaymentMethod,
      amountCharged: amountPaid,
      testName,
    };
  }

  reportPaymentNewSyntax({
    amountPaid,
    currency,
    paymentMethod: lastSuccessfulPaymentResponse?.paymentMethod,
    paymentStatus: lastSuccessfulPaymentResponse?.paymentStatus,
    trackingId,
    payPalProcessor,
    isPayPalCommerceInvoice,
    savePaymentMethodSuccessful,
    paymentMethodSubType,
    paymentRetryInfo,
  });
}

export function reportInvoicePayInCP({
  sale = {},
  data,
  intuit_tid,
  selectedPaymentMethod,
  paymentType,
  gratuityValue = 0,
  isDateScheduled = false,
  isAutoPayOn = false,
  isNewWalletUI = false,
  inputAmount,
  paymentMethodSubType,
  didSaveMerchantWalletOrError,
  payment = {},
  ixp = {},
}) {
  const { currencyInfo: { currency } = {} } = sale;

  const {
    paymentMethod,
    amountPaid,
    trackingId,
    paymentStatus = '',
    savePaymentMethodSuccessful,
  } = data;

  // Build payment retry info object for card payments only
  let paymentRetryInfo;
  if (payment.isPaymentRetry && isCardPayment(payment.paymentMethodType)) {
    const PaymentRetryExperiment = ixp?.PaymentRetryExperiment;
    const testName =
      PaymentRetryExperiment?.experimentKey === 'IXP2_541549'
        ? `${PaymentRetryExperiment.experimentId}:${PaymentRetryExperiment.treatmentKey}`
        : '';

    paymentRetryInfo = {
      isPaymentRetry: true,
      originalTotalAmount: payment.originalPaymentAmount,
      originalPaymentMethod: payment.originalPaymentMethod,
      amountCharged: amountPaid ?? inputAmount,
      testName,
    };
  }

  reportPaymentNewSyntax({
    amountPaid,
    currency,
    gratuityValue,
    inputAmount,
    intuit_tid,
    isAutoPayOn,
    isDateScheduled,
    isNewWalletUI,
    paymentMethod,
    selectedPaymentMethod,
    paymentStatus,
    paymentType,
    trackingId,
    savePaymentMethodSuccessful,
    paymentMethodSubType,
    didSaveMerchantWalletOrError,
    paymentRetryInfo,
  });
}

// the name of this method is temporary!
export const reportPaymentNewSyntax = ({
  amountPaid,
  currency,
  gratuityValue,
  intuit_tid,
  isAutoPayOn,
  isDateScheduled,
  isNewWalletUI,
  paymentMethod,
  selectedPaymentMethod,
  paymentStatus = '',
  paymentType,
  trackingId,
  error,
  payPalProcessor,
  isPayPalCommerceInvoice,
  inputAmount,
  savePaymentMethodSuccessful,
  paymentMethodSubType,
  didSaveMerchantWalletOrError,
  paymentRetryInfo,
}) => {
  let formattedPaymentMethod = paymentMethod ?? selectedPaymentMethod;
  formattedPaymentMethod = formatPaymentMethod(formattedPaymentMethod);
  const successfullStatuses = [
    'SUCCESS',
    'PENDING',
    'CAPTURED',
    'ORDER_CAPTURED',
    'success',
    'PAY_BY_BANK_APPROVED',
  ];
  const activityStatus = successfullStatuses.includes(paymentStatus)
    ? 'success'
    : paymentStatus.toLowerCase();

  splunkReporter.contextual({
    logInfo: { logLevel: 'info', logger: 'businessLogic/Insight/Reporting' },
    event: 'pay',
    action: 'submit',
    activityInfo: {
      status: activityStatus,
      amount: amountPaid ?? inputAmount,
      currency: typeof currency === 'string' ? currency.toLowerCase() : currency,
      gratuityAmount: gratuityValue,
      intuitTid: intuit_tid,
      isAutoPayOn,
      isDateScheduled,
      isNewWalletUI,
      paymentMethod: formattedPaymentMethod,
      paymentType,
      trackingId,
      payPalProcessor,
      isPayPalCommerceInvoice,
      savePaymentMethodSuccessful,
      paymentMethodSubType,
      savedMerchantWalletStatus: didSaveMerchantWalletOrError || 'N/A',
      ...(paymentRetryInfo && { paymentRetryInfo }),
    },
    error,
  });
};
